import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { assistantApi } from "../services/api";
import PageHeader from "../components/PageHeader";
import CompanyLogo from "../components/CompanyLogo";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import ChipGroup from "../components/ChipGroup";
import { timeAgo } from "../utils/format";
import {
  WORK_TYPES,
  EMPLOYMENT_TYPES,
  EXPERIENCE_LEVELS,
  LEVEL_HINTS,
  COMPANY_TYPES,
  COMPANY_TYPE_HELP,
  VISA_OPTIONS,
  VISA_LABELS,
  VISA_HELP,
} from "../data/jobFilters";

const EMPTY_FILTERS = {
  query: "",
  location: "",
  workTypes: [],
  employmentTypes: [],
  levels: [],
  visa: [],
  companyTypes: [],
  maxAgeDays: 0,
};
const TEXT_FILTERS = ["query", "location"];
const AGE_OPTIONS = [
  [0, "Any time"],
  [1, "Past 24 hours"],
  [7, "Past week"],
  [14, "Past 2 weeks"],
  [30, "Past month"],
];

const isSet = (value) => (Array.isArray(value) ? value.length > 0 : Boolean(value));

function VisaTags({ visa }) {
  if (!visa) return null;
  const tags = [];
  if (visa.sponsors === "yes") tags.push(["tag-success", "Sponsors visas", "The posting says it sponsors visas"]);
  else if (visa.sponsors === "likely") {
    tags.push(["tag-success", "H-1B sponsor", `${visa.approvals.toLocaleString()} approved H-1B petitions in recent years (USCIS)`]);
  } else if (visa.sponsors === "no") tags.push(["", "No visa sponsorship", "The posting rules out sponsorship or needs US citizenship"]);
  if (visa.f1 === "yes" && visa.sponsors !== "yes") tags.push(["tag-success", "OPT/CPT", "The posting mentions OPT, CPT or F-1 students"]);
  return tags.map(([variant, label, title]) => (
    <span key={label} className={`tag ${variant}`} title={title}>
      {label}
    </span>
  ));
}

function JobMeta({ job }) {
  return (
    <div className="d-flex flex-wrap gap-2 small text-muted">
      {job.location && (
        <span className="text-truncate" style={{ maxWidth: "100%" }}>
          <i className="bi bi-geo-alt me-1" />
          {job.location}
        </span>
      )}
      <span>
        <i className="bi bi-briefcase me-1" />
        {job.workType} · {job.employmentType}
      </span>
      {job.salary && (
        <span>
          <i className="bi bi-cash me-1" />
          {job.salary}
        </span>
      )}
    </div>
  );
}

function FindJobs() {
  const { jobs, createJob, toast, integrations } = useApp();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [preview, setPreview] = useState(null);
  const [details, setDetails] = useState({});
  const [savingId, setSavingId] = useState(null);

  const run = useCallback(async (f) => {
    setLoading(true);
    setSearchError("");
    try {
      const { jobs: found, applied: used } = await assistantApi.search(f);
      setResults(found);
      setApplied(used && { ...used, filters: f });
    } catch (err) {
      setResults([]);
      setSearchError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    run(EMPTY_FILTERS);
  }, [run]);

  const openPreview = async (job) => {
    setPreview(job);
    if (details[job.id]) return;
    setDetails((d) => ({ ...d, [job.id]: { loading: true } }));
    try {
      const { text } = await assistantApi.readPage(job.url);
      setDetails((d) => ({ ...d, [job.id]: { text } }));
    } catch {
      setDetails((d) => ({ ...d, [job.id]: { text: "" } }));
    }
  };

  const tracked = (job) => jobs.find((j) => j.url === job.url);

  // Pulls the full posting before saving so tailoring and cover letters have the real description.
  const fullDetails = async (job) => {
    const { id: _searchId, postedAt, ...basic } = job;
    if (integrations.ai) {
      try {
        const { job: parsed } = await assistantApi.parseJob({ url: job.url });
        return { ...basic, ...parsed, source: job.source, url: job.url };
      } catch {
        // Fall through to the page text.
      }
    }
    const text = details[job.id]?.text || (await assistantApi.readPage(job.url).then((p) => p.text).catch(() => ""));
    return { ...basic, description: text || basic.description };
  };

  const save = async (job, startApplying = false) => {
    setSavingId(job.id);
    try {
      const id = await createJob(await fullDetails(job));
      toast(`Saved ${job.title} at ${job.company}`);
      setPreview(null);
      if (startApplying) navigate(`/jobs/${id}?tab=apply`);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setSavingId(null);
    }
  };

  const setFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (!TEXT_FILTERS.includes(key)) run(next);
  };

  const hasFilters = Object.values(filters).some(isSet);
  // Words like "new grad" or "intern" in the search act as filters when none are picked.
  const implied =
    applied &&
    [
      applied.filters.levels.length === 0 && applied.levels.length > 0 && applied.levels.join(", "),
      applied.filters.employmentTypes.length === 0 && applied.employmentTypes.length > 0 && applied.employmentTypes.join(", "),
    ].filter(Boolean);

  return (
    <>
      <PageHeader title="Find jobs" subtitle="Search open roles and save the ones you like to your tracker." />

      <form
        className="card mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          run(filters);
        }}
      >
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-5">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search" />
                </span>
                <input
                  className="form-control"
                  placeholder="Role, skill or company"
                  value={filters.query}
                  onChange={(e) => setFilter("query", e.target.value)}
                  aria-label="Search keywords"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-geo-alt" />
                </span>
                <input
                  className="form-control"
                  placeholder="City, state or 'Remote'"
                  value={filters.location}
                  onChange={(e) => setFilter("location", e.target.value)}
                  aria-label="Location"
                />
              </div>
            </div>
            <div className="col-md-3 d-grid">
              <button className="btn btn-primary" type="submit">
                Search
              </button>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-lg-6">
              <label className="form-label small fw-medium d-block mb-1">Experience level</label>
              <ChipGroup
                name="Experience level"
                options={EXPERIENCE_LEVELS}
                hints={LEVEL_HINTS}
                value={filters.levels}
                onChange={(v) => setFilter("levels", v)}
              />
            </div>
            <div className="col-lg-6">
              <label className="form-label small fw-medium d-block mb-1" title={VISA_HELP}>
                Visa <i className="bi bi-info-circle text-muted" />
              </label>
              <ChipGroup name="Visa" options={VISA_OPTIONS} labels={VISA_LABELS} value={filters.visa} onChange={(v) => setFilter("visa", v)} />
            </div>
            <div className="col-lg-6">
              <label className="form-label small fw-medium d-block mb-1">Job type</label>
              <ChipGroup
                name="Job type"
                options={EMPLOYMENT_TYPES}
                value={filters.employmentTypes}
                onChange={(v) => setFilter("employmentTypes", v)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-medium d-block mb-1">Work type</label>
              <ChipGroup name="Work type" options={WORK_TYPES} value={filters.workTypes} onChange={(v) => setFilter("workTypes", v)} />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-medium d-block mb-1" htmlFor="find-age">
                Posted
              </label>
              <select
                id="find-age"
                className="form-select form-select-sm"
                value={filters.maxAgeDays}
                onChange={(e) => setFilter("maxAgeDays", Number(e.target.value))}
              >
                {AGE_OPTIONS.map(([days, label]) => (
                  <option key={days} value={days}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-medium d-block mb-1">Company</label>
              <ChipGroup
                name="Company"
                options={COMPANY_TYPES}
                value={filters.companyTypes}
                onChange={(v) => setFilter("companyTypes", v)}
              />
              <div className="small text-muted mt-1">{COMPANY_TYPE_HELP}</div>
            </div>
          </div>
          {hasFilters && (
            <div className="text-end mt-2">
              <button
                type="button"
                className="btn btn-link btn-sm text-muted p-0"
                onClick={() => {
                  setFilters(EMPTY_FILTERS);
                  run(EMPTY_FILTERS);
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </form>

      <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
        <span className="small text-muted">
          {loading ? "Searching…" : `${results.length} ${results.length === 1 ? "job" : "jobs"}, newest first`}
          {!loading && implied?.length > 0 && <> · showing {implied.join(" and ")} roles from your search words</>}
        </span>
        <span className="small text-muted text-end">
          {filters.companyTypes.includes("Startups") ? (
            <>
              Startups under 50 people from the{" "}
              <a href="https://speedrun-talent-network.com/developers" target="_blank" rel="noreferrer">
                a16z portfolio
              </a>
            </>
          ) : (
            <>
              From company job boards,{" "}
              <a href="https://speedrun-talent-network.com/developers" target="_blank" rel="noreferrer">
                a16z
              </a>
              ,{" "}
              <a href="https://github.com/SimplifyJobs/New-Grad-Positions" target="_blank" rel="noreferrer">
                Simplify
              </a>
              , <a href="https://remotive.com" target="_blank" rel="noreferrer">Remotive</a>
            </>
          )}
        </span>
      </div>

      {loading ? (
        <div className="d-flex flex-column gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card placeholder-glow">
              <div className="card-body">
                <span className="placeholder col-6 mb-2" />
                <span className="placeholder col-4" />
              </div>
            </div>
          ))}
        </div>
      ) : searchError ? (
        <div className="card">
          <EmptyState
            icon="bi-wifi-off"
            title="Search isn't working right now"
            text={searchError}
            action={
              <button className="btn btn-primary btn-sm" onClick={() => run(filters)}>
                Try again
              </button>
            }
          />
        </div>
      ) : results.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="bi-search"
            title="No jobs match your search"
            text="Try fewer filters or a broader keyword. Found one somewhere else? Paste its link with Add job."
          />
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {results.map((job) => {
            const saved = tracked(job);
            return (
              <div key={job.id} className="card result-card" onClick={() => openPreview(job)}>
                <div className="card-body d-flex gap-3">
                  <CompanyLogo name={job.company} size={44} />
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex justify-content-between gap-2">
                      <div className="min-w-0">
                        <div className="fw-semibold">{job.title}</div>
                        <div className="small">{job.company}</div>
                      </div>
                      {job.postedAt && <span className="small text-muted text-nowrap">{timeAgo(job.postedAt)}</span>}
                    </div>
                    <div className="mt-2">
                      <JobMeta job={job} />
                    </div>
                    <div className="d-flex flex-wrap gap-1 mt-2 align-items-center">
                      {job.startup && <span className="tag tag-success">Startup</span>}
                      {job.level && <span className="tag">{job.level}</span>}
                      <VisaTags visa={job.visa} />
                      {job.requirements.slice(0, 4).map((r) => (
                        <span key={r} className="tag">
                          {r}
                        </span>
                      ))}
                      <span className="small text-muted">via {job.source}</span>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {saved ? (
                      <Link to={`/jobs/${saved.id}`} className="btn btn-sm btn-outline-success" aria-label="Saved, open in tracker">
                        <i className="bi bi-check-lg" />
                        <span className="d-none d-md-inline ms-1">Saved</span>
                      </Link>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => save(job)}
                        disabled={savingId === job.id}
                        aria-label="Save to tracker"
                      >
                        {savingId === job.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-bookmark" />}
                        <span className="d-none d-md-inline ms-1">{savingId === job.id ? "Saving" : "Save"}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        show={!!preview}
        onClose={() => setPreview(null)}
        title="Job details"
        size="modal-lg"
        footer={
          preview &&
          (tracked(preview) ? (
            <Link to={`/jobs/${tracked(preview).id}`} className="btn btn-primary">
              Open in tracker <i className="bi bi-arrow-right" />
            </Link>
          ) : (
            <>
              <button className="btn btn-outline-secondary" onClick={() => save(preview)} disabled={!!savingId}>
                <i className="bi bi-bookmark me-1" /> Save for later
              </button>
              <button className="btn btn-primary" onClick={() => save(preview, true)} disabled={!!savingId}>
                {savingId === preview.id && <span className="spinner-border spinner-border-sm me-2" />}
                Save & start applying <i className="bi bi-arrow-right" />
              </button>
            </>
          ))
        }
      >
        {preview && (
          <>
            <div className="d-flex gap-3 mb-3">
              <CompanyLogo name={preview.company} size={52} />
              <div className="min-w-0">
                <h5 className="mb-0">{preview.title}</h5>
                <div className="mb-2">{preview.company}</div>
                <JobMeta job={preview} />
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {preview.startup && <span className="tag tag-success">Startup</span>}
                  {preview.level && <span className="tag">{preview.level}</span>}
                  <VisaTags visa={preview.visa} />
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
              <h6 className="mb-0">About the role</h6>
              <a href={preview.url} target="_blank" rel="noreferrer" className="small">
                View on {preview.source} <i className="bi bi-box-arrow-up-right" />
              </a>
            </div>
            {details[preview.id]?.loading ? (
              <div className="placeholder-glow">
                <span className="placeholder col-12 mb-1" />
                <span className="placeholder col-10 mb-1" />
                <span className="placeholder col-8" />
              </div>
            ) : (
              <p className="text-muted small" style={{ whiteSpace: "pre-line" }}>
                {details[preview.id]?.text || preview.description || "Open the posting to read the full description."}
              </p>
            )}
            {preview.requirements.length > 0 && (
              <>
                <h6>Tags</h6>
                <div className="d-flex flex-wrap gap-1">
                  {preview.requirements.map((r) => (
                    <span key={r} className="tag">
                      {r}
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}

export default FindJobs;
